import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, TrendingUp, Wallet, Package, FileBarChart2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

const validTabs = ["sales", "pnl", "cogs"];

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(requestedTab) ? requestedTab : "sales"
  );

  useEffect(() => {
    const requested = searchParams.get("tab");
    if (validTabs.includes(requested)) setActiveTab(requested);
  }, [searchParams]);

  const [period, setPeriod] = useState("today");
  const [outletId, setOutletId] = useState("all");
  const [outlets, setOutlets] = useState([]);
  const [sales, setSales] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [cogs, setCogs] = useState(null);

  useEffect(() => { api.get("/outlets").then(({data})=>setOutlets(data)); }, []);

  useEffect(() => {
    const q = `?period=${period}${outletId !== "all" ? `&outlet_id=${outletId}` : ""}`;
    Promise.all([api.get(`/reports/sales${q}`), api.get(`/reports/pnl${q}`), api.get(`/reports/cogs${q}`)])
      .then(([s, p, c]) => { setSales(s.data); setPnl(p.data); setCogs(c.data); });
  }, [period, outletId]);

  function downloadCsv() {
    const token = localStorage.getItem("mazboy_token");
    const q = `?period=${period}${outletId !== "all" ? `&outlet_id=${outletId}` : ""}`;
    fetch(`${API}/reports/sales.csv${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `sales_${period}.csv`; a.click();
      });
  }

  const outletName = (id) => outlets.find(o=>o.id===id)?.name || id;

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div data-workspace-redundant-header><h1 className="text-2xl font-extrabold font-display">Laporan Keuangan</h1></div>
          <p className="text-slate-500 text-sm">Sales · P&L · COGS dengan filter waktu & outlet</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="report-period" className="w-40"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hari Ini</SelectItem>
              <SelectItem value="yesterday">Kemarin</SelectItem>
              <SelectItem value="week">Minggu Ini</SelectItem>
              <SelectItem value="month">Bulan Ini</SelectItem>
            </SelectContent>
          </Select>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger data-testid="report-outlet" className="w-56"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Outlet</SelectItem>
              {outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="report-export-csv" onClick={downloadCsv} variant="outline"><Download className="w-4 h-4 mr-1"/>CSV</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => { if (!validTabs.includes(value)) return; setActiveTab(value); setSearchParams(prev => { const next = new URLSearchParams(prev); next.set("tab", value); return next; }, { replace: true }); }}>

        <TabsContent value="sales">
          {sales && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Gross Sales" value={rupiah(sales.gross_sales)}/>
                <StatCard label="Diskon" value={rupiah(sales.discount)}/>
                <StatCard label="Net Sales" value={rupiah(sales.net_sales)} tone="orange"/>
                <StatCard label="Gross Profit" value={rupiah(sales.gross_profit)} tone="emerald" sub={`${sales.margin_pct?.toFixed(1)}% margin`}/>
                <StatCard label="Transaksi" value={sales.transaction_count}/>
                <StatCard label="Avg Basket" value={rupiah(sales.avg_transaction)}/>
                <StatCard label="COGS" value={rupiah(sales.cogs)}/>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-base">Per Outlet</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Outlet</TableHead><TableHead className="text-right">Transaksi</TableHead><TableHead className="text-right">Sales</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(sales.by_outlet || {}).map(([oid, v]) => (
                        <TableRow key={oid}>
                          <TableCell>{outletName(oid)}</TableCell>
                          <TableCell className="text-right num">{v.count}</TableCell>
                          <TableCell className="text-right num font-semibold">{rupiah(v.sales)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Per Metode Bayar</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(sales.by_method || {}).map(([m, v]) => (
                      <div key={m} className="p-3 rounded-lg border">
                        <div className="text-xs uppercase text-slate-500">{m}</div>
                        <div className="text-lg font-extrabold num">{rupiah(v)}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="pnl">
          {pnl && (
            <Card>
              <CardHeader><CardTitle className="text-base">Profit & Loss Statement</CardTitle></CardHeader>
              <CardContent>
                <div className="max-w-md space-y-2 text-sm">
                  <Row label="Net Sales" value={rupiah(pnl.net_sales)} bold/>
                  <Row label="(-) COGS" value={rupiah(pnl.cogs)} sub/>
                  <Row label="Gross Profit" value={rupiah(pnl.gross_profit)} bold accent="emerald"/>
                  <div className="pt-2 border-t"/>
                  <Row label="(-) Operating Expense" value={rupiah(pnl.operating_expense)} sub/>
                  <div className="text-xs text-slate-500 pl-4 space-y-1">
                    {Object.entries(pnl.expense_by_category || {}).map(([k,v]) => (
                      <div key={k} className="flex justify-between"><span>{k}</span><span className="num">{rupiah(v)}</span></div>
                    ))}
                  </div>
                  <div className="pt-2 border-t"/>
                  <Row label="Net Operating Result" value={rupiah(pnl.net_operating_result)} bold accent={pnl.net_operating_result >= 0 ? "emerald" : "rose"}/>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cogs">
          {cogs && (
            <Card>
              <CardHeader><CardTitle className="text-base">Cost of Goods Sold · Pemakaian Bahan</CardTitle></CardHeader>
              <CardContent>
                <div className="mb-4 flex items-baseline gap-2">
                  <span className="text-sm text-slate-500">Total COGS:</span>
                  <span className="text-2xl font-extrabold num">{rupiah(cogs.total_cost)}</span>
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Bahan</TableHead><TableHead className="text-right">Terpakai</TableHead>
                    <TableHead className="text-right">Harga/unit</TableHead><TableHead className="text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {cogs.items?.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.name}</TableCell>
                        <TableCell className="text-right num">{r.usage_qty.toFixed(3)} {r.unit}</TableCell>
                        <TableCell className="text-right num text-slate-500">{rupiah(r.unit_cost)}</TableCell>
                        <TableCell className="text-right num font-semibold">{rupiah(r.total_cost)}</TableCell>
                      </TableRow>
                    ))}
                    {!cogs.items?.length && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">Belum ada data untuk periode ini</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "slate" }) {
  const tones = {
    orange: "text-orange-600", emerald: "text-emerald-600", rose: "text-rose-600", slate: "text-slate-900",
  };
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs uppercase text-slate-500 tracking-wide mb-1">{label}</div>
      <div className={`text-xl font-extrabold num ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </CardContent></Card>
  );
}

function Row({ label, value, bold, sub, accent }) {
  const cls = accent === "emerald" ? "text-emerald-600" : accent === "rose" ? "text-rose-600" : "";
  return (
    <div className="flex justify-between">
      <span className={sub ? "text-slate-500 pl-4" : bold ? "font-semibold" : ""}>{label}</span>
      <span className={`num ${bold ? "font-extrabold" : ""} ${cls}`}>{value}</span>
    </div>
  );
}
